/**
 * Contratos HTTP entre as superfícies e o Worker.
 *
 * Regra do §4: a superfície pública nunca envia ids de clínica ou de distribuidor.
 * O backend resolve-os a partir do token. Repare que nenhum pedido em /publico
 * transporta identificadores — é intencional.
 */

import type { Autorizacao, Clinica, Distribuidor } from './dominio.js';
import type { Idioma } from './idioma.js';

export interface ErroApi {
	erro: string;
	/** Código estável para o cliente distinguir casos sem ler texto. */
	codigo:
		| 'nao_autorizado'
		| 'token_invalido'
		| 'token_expirado'
		| 'token_usado'
		| 'pedido_invalido'
		| 'nao_encontrado'
		| 'conflito_estado'
		| 'erro_bigin'
		| 'erro_interno';
}

/* ── Superfície interna (delegado) ───────────────────────────────────── */

/** GET /api/interno/contexto — tudo o que o ecrã de preparação precisa, numa chamada. */
export interface ContextoInternoResposta {
	clinica: Clinica;
	distribuidores: Distribuidor[];
	versaoConsentimento: string;
	textoConsentimento: string;
	/** Decidido pelo país da clínica. Manda em toda a interface, não só no texto. */
	idioma: Idioma;
}

/** POST /api/interno/preparar — cria N registos em `Criado` (§7). */
export interface PrepararPedido {
	distribuidorIds: string[];
	nomeSignatario: string;
}

export interface PrepararResposta {
	sessaoId: string;
	autorizacoes: Autorizacao[];
}

/** POST /api/interno/assinar — fluxo A, assinatura in loco. */
export interface AssinarInternoPedido {
	sessaoId: string;
	/** PNG em data URL, capturado no canvas. */
	assinaturaPng: string;
}

/** POST /api/interno/link — fluxo B, gera o link de assinatura remota. */
export interface GerarLinkPedido {
	sessaoId: string;
	/** Minutos até expirar. O backend impõe o seu próprio máximo. */
	ttlMinutos?: number;
}

export interface GerarLinkResposta {
	url: string;
	expiraEm: string;
}

/* ── Superfície pública (clínica) ────────────────────────────────────── */

/**
 * GET /api/publico/sessao — o token vai no header `X-Assinatura-Token`,
 * nunca em query string (§10: o URL leva só o token, e o token não deve
 * acabar em logs de referrer).
 */
export interface SessaoPublicaResposta {
	clinica: { nome: string; nif: string | null; morada: string | null };
	nomeSignatario: string;
	distribuidores: { nome: string }[];
	versaoConsentimento: string;
	textoConsentimento: string;
	expiraEm: string;
	idioma: Idioma;
}

/** POST /api/publico/assinar — o corpo só transporta a assinatura. */
export interface AssinarPublicoPedido {
	assinaturaPng: string;
}

/* ── Resultado comum das duas vias de assinatura ─────────────────────── */

export interface AssinarResposta {
	sessaoId: string;
	assinadoEm: string;
	documentos: { autorizacaoId: string; distribuidor: string; pdfHash: string }[];
}

export const HEADER_TOKEN_PUBLICO = 'X-Assinatura-Token';
export const HEADER_SESSAO_INTERNA = 'X-Sessao-Interna';
